import { z } from 'zod';
import { categorySchema } from './category';
import { formulaDependencies } from './formula';
import { goalSchema } from './goal';
import { findCycle } from './graph';
import { itemIdSchema, projectIdSchema, scenarioIdSchema } from './ids';
import { investmentSchema, MAX_INVESTMENTS } from './investment';
import { itemSchema, type Item } from './item';
import { keyDateSchema, mapAnchors, MAX_KEY_DATES } from './key-date';
import { MAX_HORIZON_MONTHS, MAX_MC_PATHS, MIN_MC_PATHS } from './limits';
import { currencySchema, signedMoneySchema } from './money';
import { absoluteRateSpecSchema, rateSpecSchema } from './rate';
import { scheduledSchema, type Scheduled } from './scheduled';
import { yearMonthSchema } from './time';

export const CURRENT_SCHEMA_VERSION = 3;

/** Categorical colour slots (see packages/charts theme): assigned in order, never cycled. */
export const SCENARIO_COLOR_SLOTS = 8;
export const MAX_SCENARIOS = 16;

export const assumptionsSchema = z.object({
  /** Calendar month of month index 0. */
  start: yearMonthSchema,
  horizonMonths: z.number().int().min(1).max(MAX_HORIZON_MONTHS),
  startingCash: signedMoneySchema,
  currency: currencySchema,
  /** Global CPI. It cannot be CPI-relative itself. */
  cpi: scheduledSchema(absoluteRateSpecSchema),
  /** Nominal annual rate charged on negative cash (monthly = rate / 12). Omit for none. */
  overdraftRate: scheduledSchema(rateSpecSchema).optional(),
  seed: z.number().int().min(0).max(0xffffffff),
  mcPaths: z.number().int().min(MIN_MC_PATHS).max(MAX_MC_PATHS),
});
export type Assumptions = z.infer<typeof assumptionsSchema>;

export const scenarioSchema = z.object({
  id: scenarioIdSchema,
  name: z.string().trim().min(1).max(80),
  color: z
    .number()
    .int()
    .min(0)
    .max(SCENARIO_COLOR_SLOTS - 1),
  /** Items in this variant. An item referenced by several variants is *shared*. */
  itemIds: z.array(itemIdSchema).max(1000),
});
export type Scenario = z.infer<typeof scenarioSchema>;

const projectShape = z.object({
  schemaVersion: z.literal(CURRENT_SCHEMA_VERSION),
  id: projectIdSchema,
  name: z.string().trim().min(1).max(120),
  assumptions: assumptionsSchema,
  categories: z.array(categorySchema).max(200),
  items: z.record(z.string(), itemSchema),
  scenarios: z.array(scenarioSchema).max(MAX_SCENARIOS),
  baselineScenarioId: scenarioIdSchema.nullable(),
  goals: z.array(goalSchema).max(50),
  investments: z.array(investmentSchema).max(MAX_INVESTMENTS),
  keyDates: z.array(keyDateSchema).max(MAX_KEY_DATES),
});

type ProjectShape = z.infer<typeof projectShape>;
type Path = (string | number)[];

/** Every `Scheduled` value in a project with its path, for horizon checks and migrations. */
export function forEachScheduled(
  project: ProjectShape,
  visit: (path: Path, scheduled: Scheduled<unknown>) => void,
): void {
  const a = project.assumptions;
  visit(['assumptions', 'cpi'], a.cpi);
  if (a.overdraftRate) visit(['assumptions', 'overdraftRate'], a.overdraftRate);
  project.categories.forEach((c, i) => {
    if (c.inflation) visit(['categories', i, 'inflation'], c.inflation);
  });
  for (const [key, item] of Object.entries(project.items)) {
    const base: Path = ['items', key];
    switch (item.type) {
      case 'flow':
        visit([...base, 'amount'], item.amount);
        if (item.growth.kind === 'indexed' && item.growth.override)
          visit([...base, 'growth', 'override'], item.growth.override);
        if (item.growth.kind === 'rate') visit([...base, 'growth', 'rate'], item.growth.rate);
        break;
      case 'formula':
        if (item.spec.kind === 'percentOf') {
          visit([...base, 'spec', 'percent'], item.spec.percent);
        } else {
          const spec = item.spec;
          visit([...base, 'spec', 'brackets'], spec.brackets);
          if (spec.allowance) visit([...base, 'spec', 'allowance'], spec.allowance);
          if (spec.credits) visit([...base, 'spec', 'credits'], spec.credits);
          spec.deductions.forEach((d, i) => {
            const path = [...base, 'spec', 'deductions', i];
            visit([...path, 'rate'], d.rate);
            if (d.floor) visit([...path, 'floor'], d.floor);
            if (d.cap) visit([...path, 'cap'], d.cap);
          });
        }
        break;
      case 'asset':
        visit([...base, 'return'], item.return);
        break;
      case 'loan':
        visit([...base, 'rate'], item.rate);
        break;
    }
  }
}

function duplicates<T>(values: readonly T[]): T[] {
  const seen = new Set<T>();
  const dup = new Set<T>();
  for (const v of values) {
    if (seen.has(v)) dup.add(v);
    seen.add(v);
  }
  return [...dup];
}

function validateProject(p: ProjectShape, ctx: z.RefinementCtx): void {
  const issue = (path: Path, message: string): void => {
    ctx.addIssue({ code: 'custom', message, path });
  };

  const items = p.items;
  const categoryIds = new Set(p.categories.map((c) => c.id as string));

  for (const [key, item] of Object.entries(items)) {
    if (key !== item.id) issue(['items', key, 'id'], 'Item id must match its key');
  }
  for (const id of duplicates(p.scenarios.map((s) => s.id)))
    issue(['scenarios'], `Duplicate variant id "${id}"`);
  for (const id of duplicates(p.categories.map((c) => c.id)))
    issue(['categories'], `Duplicate category id "${id}"`);
  for (const id of duplicates(p.goals.map((g) => g.id)))
    issue(['goals'], `Duplicate goal id "${id}"`);
  for (const id of duplicates(p.investments.map((i) => i.id)))
    issue(['investments'], `Duplicate investment id "${id}"`);
  const investmentIds = new Set(p.investments.map((i) => i.id as string));
  for (const id of duplicates(p.keyDates.map((k) => k.id)))
    issue(['keyDates'], `Duplicate key date id "${id}"`);

  // Months tied to a key date must be resolved (`resolveKeyDates`).
  const keyDates = new Map(p.keyDates.map((k) => [k.id as string, k.month]));
  mapAnchors(p, (month, ref, path) => {
    const at = keyDates.get(ref.keyDate);
    if (at === undefined) issue([...path], `Unknown key date "${ref.keyDate}"`);
    else if (month !== at + ref.offset) issue([...path], 'Date does not match its key date');
    return month ?? null;
  });

  p.scenarios.forEach((s, i) => {
    s.itemIds.forEach((id, j) => {
      if (!(id in items)) issue(['scenarios', i, 'itemIds', j], `Unknown item "${id}"`);
    });
    for (const id of duplicates(s.itemIds))
      issue(['scenarios', i, 'itemIds'], `Item "${id}" is listed twice`);
  });

  if (p.baselineScenarioId !== null && !p.scenarios.some((s) => s.id === p.baselineScenarioId)) {
    issue(['baselineScenarioId'], 'Baseline variant does not exist');
  }

  for (const [key, item] of Object.entries(items)) {
    const base: Path = ['items', key];
    if (item.investment !== undefined && !investmentIds.has(item.investment)) {
      issue([...base, 'investment'], `Unknown investment "${item.investment}"`);
    }
    if (item.type !== 'asset' && item.category !== undefined && !categoryIds.has(item.category)) {
      issue([...base, 'category'], `Unknown category "${item.category}"`);
    }
    if ((item.type === 'flow' || item.type === 'formula') && item.account !== undefined) {
      if (items[item.account]?.type !== 'asset')
        issue([...base, 'account'], 'Transfers must target an asset account');
    }
    if (item.type === 'formula') {
      item.spec.sources.forEach((source, i) => {
        const ref = source.ref;
        const path = [...base, 'spec', 'sources', i, 'ref'];
        if (ref.type === 'item') {
          const target = items[ref.id];
          if (!target) issue(path, `Unknown item "${ref.id}"`);
          else if (target.type !== 'flow' && target.type !== 'formula')
            issue(path, 'Formulas can only reference flows and other formulas');
        } else if (ref.type === 'accountReturn') {
          if (items[ref.id]?.type !== 'asset')
            issue(path, 'Account returns must reference an asset account');
        } else if (ref.type === 'category' && !categoryIds.has(ref.id)) {
          issue(path, `Unknown category "${ref.id}"`);
        }
      });
    }
  }

  // Conservative (project-wide) cycle check; the engine re-checks per variant.
  const all = Object.values(items);
  const formulas = all.filter((i): i is Extract<Item, { type: 'formula' }> => i.type === 'formula');
  const cycle = findCycle(
    formulas.map((f) => f.id),
    (id) => {
      const f = items[id];
      return f?.type === 'formula'
        ? formulaDependencies(f, all).filter((d) => items[d]?.type === 'formula')
        : [];
    },
  );
  if (cycle) {
    issue(
      ['items', cycle[0] ?? ''],
      `Formulas depend on each other in a cycle: ${cycle.join(' → ')}`,
    );
  }

  const horizon = p.assumptions.horizonMonths;
  forEachScheduled(p, (path, s) => {
    if (s.kind !== 'schedule') return;
    s.segments.forEach((seg, i) => {
      if (seg.from >= horizon)
        issue([...path, 'segments', i, 'from'], 'Period starts after the end of the horizon');
    });
  });
}

export const projectSchema = projectShape.superRefine(validateProject);
export type Project = z.infer<typeof projectSchema>;
